import { createOrGetVisitor } from './Visitor';
import EventBus from './EventBus';
import {
  getCachedInquiry,
  getInquiry,
  getBulkInquiry,
  orderFinalized,
  isInquiryInStock
} from './Inquiry';
import { getMOQForProductId, getVariantInventoryCount, refreshInventory } from './Inventory';
import {
  addInquiry,
  deleteInquiry,
  finalizeOrder as apiFinalizeOrder,
  finalizeOrderWithRazorpayPayment as apiFinalizeOrderWithRazorpayPayment,
  getOrder as apiGetOrder,
  getOrderByOrderId as apiGetOrderByOrderId,
  getVisitorId,
  newOrder as apiNewOrder,
  receiveOrderByEmail,
  receiveOrderByWhatsapp,
  updateInquiryText,
  applyCoupon as apiApplyCoupon,
  removeCoupon as apiRemoveCoupon,
  savePaymentMode as apiSavePaymentMode,
  savePaymentType as apiSavePaymentType
} from './Api';
import { productSelectedEvent, productUnselectedEvent, getOpenId } from './Analytics';
import { getFormattedPrice } from './locale';
import { orderFinalizeQueue } from './queue';
import {
  getCompanyId,
  getCompanyExperiments,
  getExperiments,
  getSlabPricesEnableStatus,
  setVisitorId,
  getReviewOrder
} from './Amalgam';
import { getLocalStorage, putLocalStorage } from './LocalStorage';
import { getTwoDigitDecimal, calculateTax } from './Utils';
import { setSelectedShippingMode, deleteCachedShippingRates } from './ShipmentModes';
import {
  getProduct,
  getProductPictureId,
  getProductSetQuantityMultiplier,
  OPTION_TYPE
} from './Product';
import { onInquiryAddedSuccessfully } from './Events';
import { inquiryDeleted, inquiryCreated } from './Socket';
import {
  awayFromCartNativeHandler,
  onClearCartNativeHandler,
  getVisitorDetailsNativeHandler
} from '../../../../nativeConnectors/platform';
import requestLib from './Request';

let _order = null;
let getOrderPromise = null;
let finalizedOrders = [];
let newOrderPromise = null;
let didFetchOrder = false;

window.getVisitorDetailAccessToken = (accessToken, visitorId, skipPageRefresh = false) => {
  const visitorIdExists = !!getVisitorId();
  if (visitorId && visitorId.length > 0 && !visitorIdExists) {
    const tokenValidation = { validToken: true, visitorId };
    getVisitorDetailsNativeHandler(tokenValidation);
    setVisitorId(visitorId);
    if (!skipPageRefresh) {
      window.location.reload(false);
    }
    createOrGetVisitor(visitorId);
  } else {
    requestLib({
      method: 'POST',
      url: '/api/sms/validate-token',
      params: { token: accessToken }
    })
      .then((response) => {
        const tokenValidation = { validToken: true, visitorId: response.visitorId };
        getVisitorDetailsNativeHandler(tokenValidation);
        setVisitorId(response.visitorId);
        window.location.reload(false);
        return Promise.resolve(response.visitorId);
      })
      .then((visitorId) => createOrGetVisitor(visitorId))
      .catch(() => {
        const tokenValidation = { validToken: false };
        getVisitorDetailsNativeHandler(tokenValidation);
        window.history.back();
      });
  }
};

export const removeProduct = (productId, selectedProduct) => {
  if (!_order) {
    return Promise.resolve();
  }

  const orderId = _order.id;
  // Inquiry does not exist in this order, no need to remove it
  const inquiryId = getInquiryIdForProduct(productId);
  if (!inquiryId) {
    return Promise.resolve();
  }
  const { showcaseId, catalogueId } = selectedProduct || getInquiryForProduct(productId) || {};

  return deleteInquiry(inquiryId).then(() => {
    delete _order.selectedProducts[productId];
    inquiryDeleted(inquiryId, productId, getCompanyId(), catalogueId, getVisitorId(), getOpenId());

    if (!Object.keys(_order.selectedProducts).length) {
      _order = null;
      getOrderPromise = null;
      onClearCartNativeHandler(location.href);
      awayFromCartNativeHandler(location.href);
      EventBus.notify(EventBus.TYPE.ORDER_DELETED);
      EventBus.notify(EventBus.TYPE.ORDER_ENTITY_UPDATE);
    }
    productUnselectedEvent(productId, orderId, inquiryId, { showcaseId, catalogueId });
    EventBus.notify(EventBus.TYPE.ORDER_CHANGED);

    return Promise.resolve();
  });
};

export const clearSelectedOrder = () => {
  if (_order === null) {
    return;
  }
  _order = null;
  getOrderPromise = null;
  finalizedOrders = [];
  newOrderPromise = null;
  didFetchOrder = false;
  EventBus.notify(EventBus.TYPE.ORDER_CLEARED);
  EventBus.notify(EventBus.TYPE.ORDER_ENTITY_UPDATE);
};

export const removeAllProducts = () => {
  const orderId = _order.id;

  Object.keys(_order.selectedProducts || {}).forEach((productId) => {
    const inquiryId = getInquiryIdForProduct(productId);
    const { showcaseId, catalogueId } = getInquiryForProduct(productId);

    productUnselectedEvent(productId, orderId, inquiryId, { showcaseId, catalogueId });

    deleteInquiry(inquiryId).catch(() => {});
  });

  _order = null;
  setSelectedShippingMode(null);
  getOrderPromise = null;

  didFetchOrder = false;
  EventBus.notify(EventBus.TYPE.ORDER_DELETED);
  EventBus.notify(EventBus.TYPE.ORDER_ENTITY_UPDATE);
};

const addProduct = (
  productId,
  catalogueId,
  inquiryText = '',
  { pictureId, quantityCount, sentFromBulkOrderingView } = {}
) =>
  new Promise((resolve, reject) => {
    addInquiry(
      _order.id,
      productId,
      catalogueId,
      inquiryText,
      pictureId,
      quantityCount,
      sentFromBulkOrderingView
    )
      .then(({ selectedProduct, inquiry }) => {
        if (!_order.selectedProducts) {
          _order.selectedProducts = {};
        }

        _order.selectedProducts[selectedProduct.id] = selectedProduct;

        const inquiryId = _order.selectedProducts[selectedProduct.id].inquiryId;

        return getInquiry(inquiryId, false, inquiry).then(() => Promise.resolve(inquiryId));
      })
      .then((inquiryId) => {
        inquiryCreated(
          inquiryId,
          productId,
          getCompanyId(),
          catalogueId,
          getVisitorId(),
          getOpenId()
        );
        EventBus.notify(EventBus.TYPE.ORDER_CHANGED);

        // Push notification
        // sendInquiryPush(_order.id, inquiryId);

        productSelectedEvent(productId, _order.id, inquiryId);

        return Promise.resolve(productId);
      })
      .then((result) => {
        resolve(result);
      })
      .catch((err) => {
        if (err && err.reason === 'ORDER_DELETED') {
          window.location.reload();
          return;
        }

        processErrorWhenModifyingOrder(err);

        reject(err);
      });
  });

export const getInquiriesAndUpdateCache = (orderId, refresh) =>
  getBulkInquiry(orderId, refresh).then((inquiries) => {
    const inquiryPromises = [];

    Object.keys(inquiries || {}).forEach((inquiryId) => {
      inquiryPromises.push(getInquiry(inquiryId, false, inquiries[inquiryId]));
    });

    return Promise.all(inquiryPromises);
  });

export const deleteOOSInquiries = () => {
  const promises = [];

  Object.keys(_order.selectedProducts || {}).forEach((productId) => {
    if (!isInquiryInStock(getInquiryIdForProduct(productId))) {
      promises.push(
        removeProduct(productId, _order.selectedProducts[productId] || {}).catch(() => {})
      );
    }
  });

  if (!Object.keys(_order.selectedProducts || {}).length) {
    _order = null;
  }

  return Promise.all(promises);
};

export const getOrder = (passedOrder, shouldFetchBulkInquiry = true, orderId = '') => {
  if (_order) {
    return Promise.resolve(_order);
  }

  if (getOrderPromise) {
    return getOrderPromise;
  }

  if (didFetchOrder) {
    return Promise.resolve(_order);
  }

  const visitorId = getVisitorId();
  const companyId = getCompanyId();

  let cachedOrder = null;
  getOrderPromise = new Promise((resolve) => {
    let fetchOrderPromise;

    if (passedOrder) {
      fetchOrderPromise = Promise.resolve({ order: passedOrder });
    } else if (orderId) {
      const reviewOrderFromAmalgam = getReviewOrder(orderId);
      if (reviewOrderFromAmalgam) {
        fetchOrderPromise = Promise.resolve({ order: reviewOrderFromAmalgam });
      } else {
        fetchOrderPromise = apiGetOrderByOrderId({ orderId });
      }
    } else {
      fetchOrderPromise = apiGetOrder({ companyId, visitorId });
    }

    fetchOrderPromise
      .then(({ order }) => {
        didFetchOrder = true;

        if (!order || order.isFinalized) {
          resolve(null);
          getOrderPromise = null;

          if (order && order.isFinalized) {
            finalizedOrders.push(order);
          } else {
            _order = null;
          }
          return;
        }

        cachedOrder = order;

        if (shouldFetchBulkInquiry) {
          return getInquiriesAndUpdateCache(order.id);
        }

        return Promise.all(
          Object.keys(order.selectedProducts || {}).map((productId) =>
            order.selectedProducts[productId].inquiryId
              ? getInquiry(order.selectedProducts[productId].inquiryId, false)
              : Promise.resolve(null)
          )
        );
      })
      .then(() => {
        _order = cachedOrder;

        if (!_order) {
          return null;
        }

        Object.keys(_order.selectedProducts || {}).forEach((productId) => {
          if (!getInquiryForProduct(productId)) {
            removeProduct(productId, _order.selectedProducts[productId] || {}).catch(() => {});
          }
        });

        if (!Object.keys(_order.selectedProducts || {}).length) {
          _order = null;
        }

        resolve(_order);
        EventBus.notify(EventBus.TYPE.ORDER_ENTITY_UPDATE);
        getOrderPromise = null;
      });
  });

  return getOrderPromise;
};

const newOrder = (
  productId,
  catalogueId = null,
  inquiryText = '',
  { pictureId, quantityCount, sentFromBulkOrderingView } = {}
) => {
  if (newOrderPromise) {
    return newOrderPromise;
  }

  didFetchOrder = false;

  newOrderPromise = new Promise((resolve, reject) => {
    createOrGetVisitor()
      .then(() =>
        apiNewOrder(
          productId,
          catalogueId,
          inquiryText,
          pictureId,
          quantityCount,
          sentFromBulkOrderingView
        )
      )
      .then(({ order, inquiry }) =>
        Promise.all([getOrder(order), getInquiry(inquiry.id, false, inquiry)])
      )
      .then(([order, inquiry]) => {
        inquiryCreated(
          inquiry.id,
          inquiry.product.id,
          order.companyId,
          inquiry.catalogueId,
          getVisitorId(),
          getOpenId()
        );
        if (order) {
          EventBus.notify(EventBus.TYPE.NEW_ORDER);
        }

        EventBus.notify(EventBus.TYPE.ORDER_CHANGED);

        // Push notification
        // sendInquiryPush(_order.id, inquiry.id);

        productSelectedEvent(productId, _order.id, inquiry.id);

        return Promise.resolve(order);
      })
      .then((order) => {
        resolve(order);
        newOrderPromise = null;
      })
      .catch((err) => {
        reject(err);
        newOrderPromise = null;
      });
  });

  return newOrderPromise;
};

export const sendInquiry = (
  product,
  catalogueId = null,
  { pictureId, quantity } = {},
  { sendNativeEvent } = {}
) => {
  let promise;
  let quantityCount = quantity;

  if (typeof quantityCount !== 'number') {
    quantityCount = getVariantInventoryCount(product.id);
  }

  if (typeof quantityCount !== 'number') {
    quantityCount = getMOQForProductId(product.id) || 1;
  }

  if (product.isVariant() && shouldShowBulkOrderingView(product.id)) {
    pictureId = getProductPictureId(product) || pictureId;
  }

  const sentFromBulkOrderingView = !!(
    product.isVariant() && shouldShowBulkOrderingView(product.id)
  );

  if (newOrderPromise) {
    promise = newOrderPromise.then(() =>
      addProduct(product.id, catalogueId, '', {
        pictureId,
        quantityCount,
        sentFromBulkOrderingView
      })
    );
  } else {
    promise = getOrder().then((order) =>
      order
        ? addProduct(product.id, catalogueId, '', {
          pictureId,
          quantityCount,
          sentFromBulkOrderingView
        })
        : newOrder(product.id, catalogueId, '', {
          pictureId,
          quantityCount,
          sentFromBulkOrderingView
        })
    );
  }

  return promise.then(() => {
    onInquiryAddedSuccessfully(
      {
        product_id: product.id,
        product_name: product.name,
        product_price: product.getPriceString()
      },
      { callNativeHandler: sendNativeEvent }
    );
    return getOrder();
  });
};

export const getInquiryIdForProduct = (productId, fromOrder) => {
  const order = fromOrder || _order;
  if (
    order &&
    order.selectedProducts &&
    order.selectedProducts[productId] &&
    order.selectedProducts[productId].inquiryId
  ) {
    return order.selectedProducts[productId].inquiryId;
  }

  return null;
};

export const getInquiryForProduct = (productId) => {
  const inquiryId = getInquiryIdForProduct(productId);
  return getCachedInquiry(inquiryId);
};

export const initialize = () => {
  getOrder().catch(() => {});
};

export const getFinalizedOrder = () => finalizedOrders[0];

export const setInquiryText = (inquiryId, inquiryText) =>
  updateInquiryText(inquiryId, inquiryText)
    .then(() => getInquiry(inquiryId, true))
    .then((inquiry) => {
      _order.selectedProducts[inquiry.product.id].inquiry = inquiry;

      return Promise.resolve();
    });

export const getOrderProductDiscountMeta = (product) => {
  const coupon = getAppliedCoupon();

  const price = typeof product.price === 'number' ? product.price : null;
  const discountedPrice =
    typeof product.discounted_price === 'number' ? product.discounted_price : null;

  let couponPrice = null;
  if (price !== null && coupon && coupon.discountPercent) {
    couponPrice = price - (price * coupon.discountPercent) / 100;
    couponPrice = Math.round(couponPrice * 100) / 100;
  }

  let couponDiscountedPrice = null;
  if (discountedPrice !== null && coupon && coupon.discountPercent) {
    couponDiscountedPrice = discountedPrice - (discountedPrice * coupon.discountPercent) / 100;
    couponDiscountedPrice = Math.round(couponDiscountedPrice * 100) / 100;
  }

  if (coupon && coupon.applyOnDiscountedPrice && couponDiscountedPrice !== null) {
    return {
      originalPrice: product.price,
      discountedPrice: couponDiscountedPrice,
      couponDiscountPercent: coupon.discountPercent
    };
  }

  if (coupon && coupon.discountPercent) {
    return {
      originalPrice: product.price,
      discountedPrice: couponPrice,
      couponDiscountPercent: coupon.discountPercent
    };
  }

  if (discountedPrice !== null) {
    return { originalPrice: product.price, discountedPrice };
  }

  return { originalPrice: price };
};

export const getFragmentedOrderPrices = (passedOrder) => {
  const order = passedOrder || _order;
  if (!order) {
    return {};
  }

  const coupon = getAppliedCoupon(order);
  let subTotal = null;
  let discount = null;
  let discountedTotal = null;
  let additionalDiscount = null;
  let shippingCost = null;
  let total = null;
  const taxTypes = [];
  const taxTypeIds = {};
  let totalTaxedAmount = 0;

  const getFragmentedOrderPriceFromInquiry = (inquiry) => {
    let { product, itemCount = 1, parentSlabPrices } = inquiry;
    const { taxes, parentProductId } = product;

    if (!parentSlabPrices) {
      parentSlabPrices = {};
    }

    let originalPrice = null;
    let discountedPrice = null;
    let couponPrice = null;
    let { slabPrices, isSlab } = product || {};
    let productSlabPrice = null;

    if (parentProductId) {
      if (typeof isSlab !== 'boolean') {
        isSlab = parentSlabPrices.isSlab;
      }

      if (slabPrices === null) {
        slabPrices = { prices: parentSlabPrices.slabPrices };
      }

      if (typeof parentSlabPrices.isSet === 'boolean' && parentSlabPrices.isSet) {
        isSlab = false;
      }
    }

    if (typeof product.price === 'number') {
      originalPrice = product.price * itemCount;
    }

    if (isSlab && ((slabPrices || {}).prices || []).length > 0) {
      const slab = slabPrices.prices.find(
        ({ minQuantity, maxQuantity }) =>
          minQuantity <= itemCount && (maxQuantity >= itemCount || maxQuantity === -1)
      );

      if (slab) {
        productSlabPrice = slab.price * itemCount;
        originalPrice = slabPrices.prices[0].price * itemCount;
      }
    }

    if (
      typeof product.discounted_price === 'number' &&
      (coupon ? coupon.applyOnDiscountedPrice : true)
    ) {
      discountedPrice = product.discounted_price * itemCount;
    }

    if (originalPrice !== null && coupon) {
      if (coupon.applyOnDiscountedPrice && discountedPrice !== null) {
        couponPrice = getCouponPrice(discountedPrice, coupon, passedOrder);
      } else {
        couponPrice = getCouponPrice(originalPrice, coupon, passedOrder);
      }
    }

    if (originalPrice !== null) {
      if (subTotal === null) {
        subTotal = 0;
      }

      subTotal += originalPrice;
    }

    if (discountedPrice !== null || productSlabPrice !== null) {
      if (discount === null) {
        discount = 0;
      }

      if (isSlab && ((slabPrices || {}).prices || []).length > 0) {
        discount += originalPrice - productSlabPrice;
      } else {
        discount += originalPrice - discountedPrice;
      }
    }

    if (couponPrice !== null) {
      if (additionalDiscount === null) {
        additionalDiscount = 0;
      }

      if (discountedPrice !== null) {
        additionalDiscount += discountedPrice - couponPrice;
      } else {
        additionalDiscount += originalPrice - couponPrice;
      }
    }

    if (taxes && taxes.length) {
      const { taxId, taxPercentage, taxType } = taxes[0] || {};

      const taxAmount = calculateTax({ couponPrice, discountedPrice, originalPrice, taxes });

      if (!taxTypeIds[taxId]) {
        taxTypeIds[taxId] = true;
        taxTypes.push(`${taxPercentage}% ${taxType}`);
      }

      totalTaxedAmount += taxAmount;
    }
  };

  let inquiries = order.inquiries;

  if (!passedOrder) {
    inquiries = Object.keys(order.selectedProducts || {}).map(getInquiryForProduct);
  }

  if (Array.isArray(inquiries)) {
    inquiries.forEach((inquiry) => {
      if (!inquiry) {
        return;
      }
      getFragmentedOrderPriceFromInquiry(inquiry);
    });
  }

  if (passedOrder) {
    const { shippingCost: shippingCostOnOrder } = passedOrder;
    shippingCost = typeof shippingCostOnOrder === 'number' ? shippingCostOnOrder : null;
  }

  if (discount !== null) {
    discountedTotal = subTotal - discount;
  }

  if (subTotal !== null) {
    total = subTotal;
  }

  if (discount !== null) {
    total -= discount;
  }

  if (additionalDiscount !== null) {
    total -= additionalDiscount;
  }

  if (typeof shippingCost === 'number') {
    total += shippingCost;
  }

  total += totalTaxedAmount;

  return {
    subTotal,
    discount: discount === 0 ? null : discount,
    discountedTotal: additionalDiscount !== null ? discountedTotal : null,
    additionalDiscount,
    taxTypes,
    shippingCost,
    taxedAmount: totalTaxedAmount,
    total
  };
};

export const getTotalPrice = () => {
  const { total } = getFragmentedOrderPrices();
  return total || 0;
};

export const getFormattedTotalPrice = () => {
  const totalPrice = getTotalPrice();
  return getFormattedPrice(totalPrice);
};

const finalizeOrder = ({
  visitorBillingDetails,
  customerBillingDetails,
  checkoutFields,
  shippingAddress,
  payUsing,
  payUsingWithPartialCOD,
  shippingModeId,
  orderFrom
}) =>
  getOrder()
    .then((order) =>
      apiFinalizeOrder({
        orderId: order.id,
        visitorBillingDetails,
        customerBillingDetails,
        checkoutFields,
        shippingAddress,
        payUsing,
        payUsingWithPartialCOD,
        shippingModeId,
        orderFrom
      })
    )
    .then((response) => {
      if (response) {
        const { type } = response;
        if (type === 'ASK_FOR_PAYMENT') {
          return Promise.resolve({
            shouldAskForPayment: true,
            paymentMethodWithPartialCOD: response.paymentMethodWithPartialCOD
          });
        }

        if (type === 'ASK_FOR_RAZORPAY_PAYMENT') {
          return Promise.resolve({
            shouldAskForRazorpayPayment: true,
            razorpayOrderId: response.razorpayOrderId,
            razorpayApiKey: response.razorpayApiKey,
            partnerAccountId: response.partnerAccountId
          });
        }

        if (type === 'ASK_FOR_MERCADO_PAGO_PAYMENT') {
          return Promise.resolve({
            shouldAskForMercadoPagoPayment: true,
            mercadoPagoPublicKey: response.mercadoPagoPublicKey,
            payableAmount: response.payableAmount,
            isPartialCOD: response.isPartialCOD
          });
        }
      }

      orderFinalized();

      getOrderPromise = null;
      finalizedOrders.push(_order);
      _order = null;
      setSelectedShippingMode(null);
      refreshInventory();

      EventBus.notify(EventBus.TYPE.ORDER_FINALIZED);
      EventBus.notify(EventBus.TYPE.ORDER_ENTITY_UPDATE);

      putLocalStorage('hasConfirmedOrderAtLeastOnce', true);

      return Promise.resolve({ shouldAskForPayment: false });
    });

const savePaymentMode = ({ paymentMode }) =>
  getOrder()
    .then((order) => {
      if (_order.paymentMode === paymentMode) {
        return Promise.resolve();
      }
      return apiSavePaymentMode({
        orderId: order.id,
        paymentMode
      });
    })
    .then(() => {
      if (_order.paymentMode === paymentMode) {
        // do nothing
      } else if (_order.paymentMode === 'PARTIAL_COD') {
        _order.additionalPaymentMode = paymentMode;
      } else {
        _order.additionalPaymentMode = null;
        _order.paymentMode = paymentMode;
      }
      EventBus.notify(EventBus.TYPE.ORDER_CHANGED);
      return Promise.resolve();
    });

const savePaymentType = ({ paymentType }) =>
  getOrder()
    .then((order) => {
      if (order.paymentType === paymentType) {
        return Promise.resolve();
      }
      return apiSavePaymentType({
        orderId: order.id,
        paymentType
      });
    })
    .then(() => {
      _order.paymentType = paymentType;
      if (paymentType === 'PARTIAL' && _order.paymentMode === 'PARTIAL_COD') {
        // do nothing
      } else if (paymentType === 'PARTIAL') {
        _order.additionalPaymentMode =
          _order.paymentMode &&
          _order.paymentMode !== 'PARTIAL_COD' &&
          _order.paymentMode !== 'CASH_ON_DELIVERY'
            ? _order.paymentMode
            : null;
        _order.paymentMode = 'PARTIAL_COD';
      } else if (paymentType === 'FULL') {
        if (_order.paymentMode === 'PARTIAL_COD') {
          _order.paymentMode = _order.additionalPaymentMode || null;
          _order.additionalPaymentMode = null;
        }
      } else {
        _order.paymentMode = null;
      }
      EventBus.notify(EventBus.TYPE.ORDER_CHANGED);
      return Promise.resolve();
    });

const finalizeOrderWithRazorpayPayment = (razorpayResponse) =>
  getOrder()
    .then((order) =>
      apiFinalizeOrderWithRazorpayPayment({
        orderId: order.id,
        ...razorpayResponse
      })
    )
    .then(() => {
      orderFinalized();

      getOrderPromise = null;
      finalizedOrders.push(_order);
      _order = null;
      setSelectedShippingMode(null);
      refreshInventory();

      EventBus.notify(EventBus.TYPE.ORDER_FINALIZED);
      EventBus.notify(EventBus.TYPE.ORDER_ENTITY_UPDATE);

      putLocalStorage('hasConfirmedOrderAtLeastOnce', true);

      return Promise.resolve({ shouldAskForPayment: false });
    });

export const receiveByEmail = (orderId, { emails } = {}) =>
  receiveOrderByEmail(orderId, { emails });

export const receiveByWhatsapp = (orderId) => receiveOrderByWhatsapp(orderId);

export const pushSavePaymentMode = ({ paymentMode }) =>
  new Promise((resolve, reject) => {
    orderFinalizeQueue.push(
      { actionFn: savePaymentMode, params: [{ paymentMode }] },
      (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      }
    );
  });

export const pushSavePaymentType = ({ paymentType }) =>
  new Promise((resolve, reject) => {
    orderFinalizeQueue.push(
      { actionFn: savePaymentType, params: [{ paymentType }] },
      (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      }
    );
  });

export const pushFinalizeOrder = ({
  shippingAddressId,
  resellerUserId,
  visitorBillingDetails,
  customerBillingDetails,
  checkoutFields,
  shippingAddress,
  payUsing,
  payUsingWithPartialCOD,
  shippingModeId,
  orderFrom
}) =>
  new Promise((resolve, reject) => {
    orderFinalizeQueue.push(
      {
        actionFn: finalizeOrder,
        params: [
          {
            shippingAddressId,
            resellerUserId,
            visitorBillingDetails,
            customerBillingDetails,
            checkoutFields,
            shippingAddress,
            payUsing,
            payUsingWithPartialCOD,
            shippingModeId,
            orderFrom
          }
        ]
      },
      (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      }
    );
  });

export const pushFinalizeOrderWithRazorpayPayment = (razorpayResponse) =>
  new Promise((resolve, reject) => {
    orderFinalizeQueue.push(
      { actionFn: finalizeOrderWithRazorpayPayment, params: [razorpayResponse] },
      (err, result) => {
        if (err) {
          reject(err);
        } else {
          resolve(result);
        }
      }
    );
  });

export const applyCoupon = ({ code }) =>
  getOrder()
    .then((order) => apiApplyCoupon({ code, orderId: order.id }))
    .then(({ coupon }) => {
      if (coupon) {
        _order.appliedCoupon = coupon;
        EventBus.notify(EventBus.TYPE.ORDER_CHANGED);
      }

      return Promise.resolve({ coupon });
    });

export const removeCoupon = () =>
  getOrder()
    .then((order) => apiRemoveCoupon({ orderId: order.id }))
    .then(() => {
      _order.appliedCoupon = null;
      EventBus.notify(EventBus.TYPE.ORDER_CHANGED);
    });

export const getAppliedCoupon = (passedOrder) => {
  const order = passedOrder || _order;
  return order && order.appliedCoupon ? order.appliedCoupon : null;
};

export const hasConfirmedOrderAtLeastOnce = () =>
  getLocalStorage('hasConfirmedOrderAtLeastOnce') || false;

const getOrderTotals = (coupon, passedOrder) => {
  const order = passedOrder || _order;
  let expectedOrderTotal = 0;

  const calculateExpectedOrderTotalForInquiry = (inquiry) => {
    if (!inquiry) {
      return;
    }
    const { product, itemCount = 1 } = inquiry;

    if (typeof product.discounted_price === 'number' && coupon.applyOnDiscountedPrice) {
      expectedOrderTotal += product.discounted_price * itemCount;
    } else if (typeof product.price === 'number') {
      expectedOrderTotal += product.price * itemCount;
    }
  };

  if (passedOrder) {
    if (Array.isArray(order.inquiries)) {
      order.inquiries.forEach((inquiry) => calculateExpectedOrderTotalForInquiry(inquiry));
    }
  } else if (_order) {
    Object.keys(order.selectedProducts || {}).forEach((productId) =>
      calculateExpectedOrderTotalForInquiry(getInquiryForProduct(productId))
    );
  }

  let actualOrderTotal;
  if (coupon.discountPercent) {
    actualOrderTotal = getTwoDigitDecimal(
      expectedOrderTotal - (expectedOrderTotal * coupon.discountPercent) / 100
    );
  } else if (coupon.discountAmount) {
    actualOrderTotal = getTwoDigitDecimal(expectedOrderTotal - coupon.discountAmount);
  } else {
    actualOrderTotal = expectedOrderTotal;
  }

  if (actualOrderTotal < 0) {
    actualOrderTotal = 0;
  }

  return { expectedOrderTotal, actualOrderTotal };
};

export const getCouponPrice = (price, coupon, passedOrder) => {
  if (coupon.discountPercent) {
    return getTwoDigitDecimal(price - (price * coupon.discountPercent) / 100);
  }

  if (coupon.discountAmount) {
    const { expectedOrderTotal, actualOrderTotal } = getOrderTotals(coupon, passedOrder);
    return getTwoDigitDecimal((price * actualOrderTotal) / (expectedOrderTotal || 1));
  }

  return price;
};

const getMinOrderAmount = () => {
  const { minOrderAmount } = getCompanyExperiments() || {};
  return minOrderAmount || null;
};

export const getMoaDiff = ({ priceForMoaCalc: price } = {}) => {
  let priceForMoaCalc = price;

  if (typeof price !== 'number') {
    priceForMoaCalc = getTotalPriceForMoa();
  }

  if (typeof priceForMoaCalc !== 'number') {
    return {
      hasMinAmountInCart: true
    };
  }

  const minOrderAmount = getMinOrderAmount() || 0;

  const diff = minOrderAmount - priceForMoaCalc;

  return {
    hasMinAmountInCart: minOrderAmount <= priceForMoaCalc,
    diff,
    minOrderAmount,
    formattedDiffAmount: diff > 0 ? getFormattedPrice(diff) : '',
    formattedMOA: minOrderAmount > 0 ? getFormattedPrice(minOrderAmount) : ''
  };
};

export const getTotalPriceForMoa = () => {
  if (!_order) {
    return {};
  }

  const coupon = getAppliedCoupon();

  let subTotal = null;
  let discount = null;
  let additionalDiscount = null;
  let total = null;

  Object.keys(_order.selectedProducts || {}).forEach((productId) => {
    const inquiry = getInquiryForProduct(productId);

    if (!inquiry) {
      return;
    }

    const { product, itemCount = 1 } = inquiry;

    let originalPrice = null;
    let discountedPrice = null;
    let couponPrice = null;

    if (typeof product.price === 'number') {
      originalPrice = product.price * itemCount;
    }

    if (
      typeof product.discounted_price === 'number' &&
      (coupon ? coupon.applyOnDiscountedPrice : true)
    ) {
      discountedPrice = product.discounted_price * itemCount;
    }

    if (originalPrice !== null && coupon) {
      if (coupon.applyOnDiscountedPrice && discountedPrice !== null) {
        couponPrice = getCouponPrice(discountedPrice, coupon);
      } else {
        couponPrice = getCouponPrice(originalPrice, coupon);
      }
    }

    if (originalPrice !== null) {
      if (subTotal === null) {
        subTotal = 0;
      }

      subTotal += originalPrice;
    }

    if (discountedPrice !== null) {
      if (discount === null) {
        discount = 0;
      }

      discount += originalPrice - discountedPrice;
    }

    if (couponPrice !== null) {
      if (additionalDiscount === null) {
        additionalDiscount = 0;
      }

      if (discountedPrice !== null) {
        additionalDiscount += discountedPrice - couponPrice;
      } else {
        additionalDiscount += originalPrice - couponPrice;
      }
    }
  });

  if (subTotal !== null) {
    total = subTotal;
  }

  if (discount !== null) {
    total -= discount;
  }

  if (additionalDiscount !== null) {
    total -= additionalDiscount;
  }

  return total;
};

export const getCachedOrder = () => _order;

export const getCachedOrderId = () => (_order || {}).id || null;

export const shouldShowBulkOrderingView = (productId) => {
  if (!productId) {
    return false;
  }
  const bulkOrderingKeyName = (getExperiments().bulkOrderingView || {}).id;
  if (typeof bulkOrderingKeyName !== 'string' && !bulkOrderingKeyName) {
    return false;
  }

  if (getSlabPricesEnableStatus(productId)) {
    return false;
  }

  const product = getProduct(productId);
  if (!product) {
    return false;
  }
  const parentProduct = getProduct(product.parentProductId);
  if (!parentProduct) {
    return false;
  }
  const { allOptions = {} } = parentProduct || {};
  const productBulkOrderKeyName = Object.keys(allOptions).filter(
    (option) => bulkOrderingKeyName === option && allOptions[option] && allOptions[option].length
  );
  return !!productBulkOrderKeyName.length;
};

export const shouldShowBulkOrderingViewCheckboxes = ({ productId }) => {
  const product = getProduct(productId);

  if (!product) {
    return false;
  }

  if (getSlabPricesEnableStatus(product.id)) {
    return false;
  }

  const isBulkOrderingEnabledOnSize =
    (getExperiments().bulkOrderingView || {}).id === OPTION_TYPE.SIZES.id;

  if (
    product.optionsMeta &&
    shouldShowBulkOrderingView(product.id) &&
    product.optionsMeta[OPTION_TYPE.COLORS.id] &&
    product.optionsMeta[OPTION_TYPE.SIZES.id] &&
    Object.keys(product.optionsMeta).length === 2 &&
    isBulkOrderingEnabledOnSize
  ) {
    return true;
  }

  return false;
};

export const handleOrderFinalized = () => {
  deleteCachedShippingRates();
  setSelectedShippingMode();
};

export const getInquiryCount = (selectedProducts) =>
  Object.keys(selectedProducts || {})
    .map((row) => {
      const inquiry = getInquiryForProduct(row);
      if (inquiry) {
        const multiplier = getProductSetQuantityMultiplier({
          product: inquiry.product
        });
        return inquiry.itemCount * multiplier;
      }
      return 0;
    })
    .reduce((accumulator, currentValue) => accumulator + currentValue, 0);

export const processErrorWhenModifyingOrder = (
  err,
  { orderAlreadyConfirmedError = true, onDismiss, containerClass } = {}
) => {
  if (!err || err.reason !== 'ORDER_ALREADY_CONFIRMED') {
    return;
  }
  EventBus.notify(EventBus.TYPE.ORDER_CONFIRMED_ERROR, {
    orderAlreadyConfirmedError,
    onDismiss,
    containerClass
  });
};

export const isPartialCODAvailable = () => {
  const totalPrice = getTotalPrice();

  return !!(
    _order &&
    _order.paymentMethods &&
    _order.paymentMethods.PARTIAL_COD &&
    totalPrice &&
    totalPrice > _order.partialCODAmount
  );
};
